import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, Button, IconButton,
  Tooltip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ─── Tilt interpretation bands (mirrors SlantingAnalysisModal / API spec) ───
const TILT_BANDS = [
  { min: 0, max: 0.5, label: 'Essentially Vertical', color: '#4caf50' },
  { min: 0.5, max: 2.0, label: 'Minor Tilt (Monitor)', color: '#8bc34a' },
  { min: 2.0, max: 5.0, label: 'Notable Tilt (Investigate)', color: '#ff9800' },
  { min: 5.0, max: Infinity, label: 'Significant Tilt (Immediate)', color: '#f44336' },
];
const getTiltBand = (angle) => {
  const a = Math.abs(angle || 0);
  return TILT_BANDS.find(b => a >= b.min && a < b.max) || TILT_BANDS[TILT_BANDS.length - 1];
};

const NAVY = '#003366';

const fmtDate = (d) => {
  if (!d) return 'N/A';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? 'N/A' : dt.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ─── 2D side-view tilt diagram (pure SVG; tilt is exaggerated for clarity, true value labelled) ───
const TiltDiagram = ({ angle, directionDeg, compass, band }) => {
  const trueAngle = Math.abs(angle || 0);
  // Exaggerate small tilts so the lean is visible, but cap it; the true value is labelled separately.
  const displayAngle = Math.min(Math.max(trueAngle * 4, 5), 30);
  const rad = (displayAngle * Math.PI) / 180;

  // Side-view geometry
  const baseX = 150, baseY = 300, poleLen = 230;
  const tipX = baseX + poleLen * Math.sin(rad);
  const tipY = baseY - poleLen * Math.cos(rad);
  const refTipX = baseX, refTipY = baseY - poleLen;

  // Angle arc between vertical reference and pole
  const arcR = 70;
  const arcStartX = baseX, arcStartY = baseY - arcR;
  const arcEndX = baseX + arcR * Math.sin(rad), arcEndY = baseY - arcR * Math.cos(rad);

  // Compass geometry (separate panel on the right)
  const cX = 470, cY = 150, cR = 70;
  const bearing = directionDeg || 0;
  const bRad = (bearing * Math.PI) / 180;
  const arrTipX = cX + cR * 0.8 * Math.sin(bRad);
  const arrTipY = cY - cR * 0.8 * Math.cos(bRad);

  return (
    <svg viewBox="0 0 580 360" style={{ width: '100%', height: 'auto' }}>
      {/* Ground */}
      <line x1={40} y1={baseY} x2={300} y2={baseY} stroke="#888" strokeWidth={3} />
      {[...Array(7)].map((_, i) => (
        <line key={i} x1={50 + i * 38} y1={baseY} x2={40 + i * 38} y2={baseY + 12} stroke="#aaa" strokeWidth={2} />
      ))}
      {/* Vertical reference (dashed) */}
      <line x1={baseX} y1={baseY} x2={refTipX} y2={refTipY} stroke="#9e9e9e" strokeWidth={3} strokeDasharray="8,6" />
      <text x={refTipX - 4} y={refTipY - 8} fill="#9e9e9e" fontSize={13} textAnchor="end">vertical</text>
      {/* Angle arc */}
      <path d={`M ${arcStartX} ${arcStartY} A ${arcR} ${arcR} 0 0 1 ${arcEndX} ${arcEndY}`} fill="none" stroke={band.color} strokeWidth={3} />
      {/* Pole (tilted) */}
      <line x1={baseX} y1={baseY} x2={tipX} y2={tipY} stroke={band.color} strokeWidth={12} strokeLinecap="round" />
      <circle cx={baseX} cy={baseY} r={7} fill={NAVY} />
      {/* True angle label near the arc */}
      <text x={baseX + arcR + 12} y={baseY - arcR + 18} fill={band.color} fontSize={26} fontWeight="bold">
        {trueAngle.toFixed(1)}°
      </text>

      {/* Compass panel */}
      <circle cx={cX} cy={cY} r={cR} fill="#fff" stroke="#ccc" strokeWidth={2} />
      <text x={cX} y={cY - cR + 16} fill="#555" fontSize={13} textAnchor="middle" fontWeight="bold">N</text>
      <text x={cX} y={cY + cR - 6} fill="#999" fontSize={12} textAnchor="middle">S</text>
      <text x={cX + cR - 8} y={cY + 4} fill="#999" fontSize={12} textAnchor="middle">E</text>
      <text x={cX - cR + 8} y={cY + 4} fill="#999" fontSize={12} textAnchor="middle">W</text>
      {compass ? (
        <>
          <line x1={cX} y1={cY} x2={arrTipX} y2={arrTipY} stroke={NAVY} strokeWidth={4} strokeLinecap="round" />
          <circle cx={arrTipX} cy={arrTipY} r={6} fill={NAVY} />
          <text x={cX} y={cY + cR + 22} fill={NAVY} fontSize={15} textAnchor="middle" fontWeight="bold">
            Tilt direction: {compass}{directionDeg != null ? ` (${Math.round(directionDeg)}°)` : ''}
          </text>
        </>
      ) : (
        <text x={cX} y={cY + cR + 22} fill="#999" fontSize={13} textAnchor="middle">Direction: N/A (no heading)</text>
      )}
    </svg>
  );
};

// ─── Horizontal classification band scale with a marker at the measured value ───
const BandScale = ({ angle }) => {
  const stops = [0, 0.5, 2, 5, 8]; // last is a visual cap for the open-ended band
  const cap = 8;
  const pct = (v) => `${Math.min((v / cap) * 100, 100)}%`;
  const segs = [
    { from: 0, to: 0.5, color: '#4caf50' },
    { from: 0.5, to: 2, color: '#8bc34a' },
    { from: 2, to: 5, color: '#ff9800' },
    { from: 5, to: cap, color: '#f44336' },
  ];
  const markerPct = Math.min((Math.abs(angle || 0) / cap) * 100, 100);
  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ position: 'relative', height: 18, borderRadius: 1, overflow: 'hidden', display: 'flex' }}>
        {segs.map((s, i) => (
          <Box key={i} sx={{ width: `${((s.to - s.from) / cap) * 100}%`, bgcolor: s.color }} />
        ))}
        {/* Marker */}
        <Box sx={{ position: 'absolute', top: -4, left: `calc(${markerPct}% - 6px)`, width: 0, height: 0,
          borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: `9px solid ${NAVY}` }} />
      </Box>
      <Box sx={{ position: 'relative', height: 16, mt: 0.25 }}>
        {stops.map((v, i) => (
          <Typography key={i} variant="caption" sx={{ position: 'absolute', left: pct(v), transform: 'translateX(-50%)', color: '#666', fontSize: '0.6rem' }}>
            {v === cap ? '5+' : v}°
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
        {TILT_BANDS.map((b) => (
          <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: b.color }} />
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#444' }}>{b.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const PAGE_SX = {
  width: '210mm',
  minHeight: '297mm',
  bgcolor: '#fff',
  mx: 'auto',
  mb: 2,
  boxShadow: 3,
  position: 'relative',
  boxSizing: 'border-box',
  overflow: 'hidden',
};

const SlantingReport = ({ open, onClose, asset, analysisData, generatedBy }) => {
  const reportRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  const summary = analysisData?.summary || {};
  const frames = analysisData?.frames || [];
  const band = getTiltBand(summary.max_tilt_degrees);
  const reportDate = new Date();

  const Row = ({ label, children, valueColor }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '150px' }}>{label}:</Typography>
      <Typography variant="body2" sx={{ color: valueColor }}>{children}</Typography>
    </Box>
  );

  const generatePDF = async () => {
    if (!reportRef.current) return;
    const pages = reportRef.current.querySelectorAll('.slanting-page');
    if (!pages.length) return;
    setGenerating(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const W = 210, H = 297;
      for (let i = 0; i < pages.length; i++) {
        const canvas = await html2canvas(pages[i], { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
        const img = canvas.toDataURL('image/jpeg', 0.92);
        if (i > 0) pdf.addPage();
        pdf.addImage(img, 'JPEG', 0, 0, W, H);
      }
      pdf.save(`slanting-report-${asset?.code || asset?.id || 'asset'}.pdf`);
    } catch (err) {
      console.error('Failed to generate slanting report PDF:', err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: '#e4e6eb' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: NAVY }}>Slanting Analysis Report</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={generating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DownloadIcon />}
            onClick={generatePDF}
            disabled={generating}
            sx={{ mr: 1, bgcolor: NAVY, '&:hover': { bgcolor: '#002244' } }}
          >
            {generating ? 'Generating…' : 'Download PDF'}
          </Button>
          <Tooltip title="Close">
            <IconButton onClick={onClose}><CloseIcon /></IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <Box ref={reportRef}>
          {/* ───────────── PAGE 1: COVER ───────────── */}
          <Box className="slanting-page" sx={PAGE_SX}>
            <Box sx={{ p: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '297mm' }}>
              <Typography sx={{ color: NAVY, fontWeight: 600, fontSize: '2.1rem', textAlign: 'center', mb: 4 }}>
                High Mast Slanting Analysis Report
              </Typography>

              <Box sx={{ width: '100%', maxWidth: '520px', mb: 4, p: 3, border: `2px solid ${NAVY}`, borderRadius: 2, bgcolor: 'rgba(0,51,102,0.02)' }}>
                <Typography variant="h6" sx={{ color: NAVY, fontWeight: 600, mb: 2, textAlign: 'center' }}>
                  Report Summary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Row label="Asset ID">{asset?.code || asset?.name || 'N/A'}</Row>
                  {asset?.name && asset?.code && <Row label="Asset Name">{asset.name}</Row>}
                  <Row label="Network / Section">{[asset?.network_name, asset?.section_name].filter(Boolean).join(' • ') || 'N/A'}</Row>
                  <Row label="Max Tilt" valueColor={band.color}>
                    <strong>{(summary.max_tilt_degrees != null ? Number(summary.max_tilt_degrees).toFixed(2) : '—')}°</strong>
                  </Row>
                  <Row label="Classification" valueColor={band.color}>
                    <strong>{summary.classification_label || band.label}</strong>
                  </Row>
                  <Row label="Tilt Direction">
                    {summary.tilt_direction_compass
                      ? `${summary.tilt_direction_compass}${summary.tilt_direction_degrees != null ? ` (${Math.round(summary.tilt_direction_degrees)}°)` : ''}`
                      : (summary.tilt_direction_degrees != null ? `${Math.round(summary.tilt_direction_degrees)}°` : 'N/A')}
                  </Row>
                  <Row label="Confidence">{summary.confidence || 'N/A'}</Row>
                  <Row label="Frames Analyzed">{frames.length || analysisData?.image_count || 0}</Row>
                  {analysisData?.model_version && <Row label="Model Version">{analysisData.model_version}</Row>}
                  <Row label="Analyzed">{fmtDate(analysisData?.completed_at)}</Row>
                  <Row label="Generated">{fmtDate(reportDate)}</Row>
                </Box>
              </Box>

              {generatedBy && (
                <Typography variant="body2" sx={{ color: '#666' }}>Report generated by: {generatedBy}</Typography>
              )}
            </Box>
            {/* Right side stripes (matches the standard inspection report cover) */}
            <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '30px', bgcolor: '#4CAF50' }} />
            <Box sx={{ position: 'absolute', top: 0, right: '35px', bottom: 0, width: '30px', bgcolor: '#0D47A1' }} />
            <Typography variant="caption" sx={{ position: 'absolute', bottom: 12, left: 16, color: '#999' }}>Page 1</Typography>
          </Box>

          {/* ───────────── PAGE 2: TILT VISUALIZATION ───────────── */}
          <Box className="slanting-page" sx={PAGE_SX}>
            <Box sx={{ p: 5, height: '297mm', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h5" sx={{ color: NAVY, fontWeight: 600, mb: 1 }}>Tilt Visualization</Typography>
              <Box sx={{ height: '2px', bgcolor: NAVY, mb: 3, opacity: 0.2 }} />

              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: band.color }}>
                  {summary.max_tilt_degrees != null ? Number(summary.max_tilt_degrees).toFixed(2) : '—'}°
                </Typography>
                <Typography variant="h6" sx={{ color: band.color, fontWeight: 600 }}>
                  {summary.classification_label || band.label}
                </Typography>
              </Box>

              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 3, bgcolor: '#fafafa' }}>
                <TiltDiagram
                  angle={summary.max_tilt_degrees}
                  directionDeg={summary.tilt_direction_degrees}
                  compass={summary.tilt_direction_compass}
                  band={band}
                />
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#888', mt: 1 }}>
                  Lean shown illustratively for clarity; the measured tilt is the value labelled above.
                </Typography>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: NAVY, mb: 0.5 }}>Severity Scale</Typography>
                <BandScale angle={summary.max_tilt_degrees} />
              </Box>

              {summary.confidence_note && (
                <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#666', fontStyle: 'italic' }}>
                  Note: {summary.confidence_note}
                </Typography>
              )}
            </Box>
            <Typography variant="caption" sx={{ position: 'absolute', bottom: 12, right: 16, color: '#999' }}>Page 2</Typography>
          </Box>

          {/* ───────────── PAGE 3: FRAME ANALYSIS ───────────── */}
          <Box className="slanting-page" sx={PAGE_SX}>
            <Box sx={{ p: 5, height: '297mm', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h5" sx={{ color: NAVY, fontWeight: 600, mb: 1 }}>
                Frame Analysis ({frames.length} {frames.length === 1 ? 'Frame' : 'Frames'})
              </Typography>
              <Box sx={{ height: '2px', bgcolor: NAVY, mb: 3, opacity: 0.2 }} />

              {frames.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, color: NAVY, borderBottom: `2px solid ${NAVY}` } }}>
                      <TableCell>#</TableCell>
                      <TableCell align="right">Tilt</TableCell>
                      <TableCell align="right">Viewing Angle</TableCell>
                      <TableCell align="right">Gimbal Pitch</TableCell>
                      <TableCell>Heading</TableCell>
                      <TableCell align="right">Confidence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {frames.map((f, i) => {
                      const fb = getTiltBand(f.tilt_angle_degrees);
                      return (
                        <TableRow key={i}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: fb.color }}>
                            {f.tilt_angle_degrees != null ? `${Number(f.tilt_angle_degrees).toFixed(1)}°` : '—'}
                          </TableCell>
                          <TableCell align="right">{f.viewing_angle_degrees != null ? `${Math.round(f.viewing_angle_degrees)}°` : '—'}</TableCell>
                          <TableCell align="right">{f.gimbal_pitch_degrees != null ? `${Number(f.gimbal_pitch_degrees).toFixed(1)}°` : '—'}</TableCell>
                          <TableCell>
                            {f.heading_degrees != null ? `${Math.round(f.heading_degrees)}°${f.heading_compass ? ` (${f.heading_compass})` : ''}` : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ textTransform: 'capitalize' }}>
                            {typeof f.confidence_score === 'number' ? `${Math.round(f.confidence_score * 100)}%` : (f.confidence_score || '—')}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">No per-frame data available.</Typography>
              )}

              <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#888' }}>
                Tilt colours follow the severity scale (green → red). Max tilt across frames:{' '}
                <strong style={{ color: band.color }}>
                  {summary.max_tilt_degrees != null ? `${Number(summary.max_tilt_degrees).toFixed(2)}°` : '—'}
                </strong>.
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ position: 'absolute', bottom: 12, right: 16, color: '#999' }}>Page 3</Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SlantingReport;
